import { useCallback, useEffect, useState } from 'react';
import { RadioList } from '@components/radio-list';

import { ModalWithLayout } from '../../modal-with-layout';

import { type RadioListModalProps } from './radio-list-modal.type';

export const RadioListModal = ({
  // ModalWithLayout base props
  isOpen,
  onClose,
  title,
  subtitle,
  variant,
  closeOnBackdropClick,
  closeOnEscape,
  onClickEditTitle,
  showCloseButton = true,
  bordered = true,
  primaryIcon,
  primaryIconClassName,
  primaryIconColor,
  onClickPrimaryIcon,
  onPrimaryClick,
  onSecondaryClick,
  contentClassName,

  // Radio list props
  options,
  value,
  defaultValue = undefined,
  onChange,
  enableNewOption = false,
  newOptionLabel,
  newOptionPlaceholder,
  newMaxLength,
  newError,
  newOptionHelper,
  radioName,

  // Footer props
  primaryButtonText,
  secondaryButtonText,
  isPrimaryDisabled,
  isPrimaryLoading,
}: RadioListModalProps) => {
  // Determine initial selection based on value or defaultValue
  const selectionSource = value ?? defaultValue;
  const initialSelection = selectionSource ? selectionSource.id ?? 'new' : null;

  // Track current selection for primary button logic
  const [currentSelection, setCurrentSelection] = useState<string | null>(initialSelection);
  const [currentNewValue, setCurrentNewValue] = useState<string>(value?.name ?? defaultValue?.name ?? '');

  // Sync internal state with props when modal opens or value changes
  useEffect(() => {
    if (isOpen) {
      const source = value ?? defaultValue;
      const newSelection = source ? source.id ?? 'new' : null;
      setCurrentSelection(newSelection);
      setCurrentNewValue(value?.name ?? defaultValue?.name ?? '');
    }
  }, [isOpen, value, defaultValue]);

  const handleSelectionChange = (next: string | null) => {
    setCurrentSelection(next);

    if (next === 'new') {
      setCurrentNewValue('');
      onChange?.({ name: '' });
      return;
    }

    const selectedOption = options?.find((option) => option.id === next);
    if (selectedOption) {
      onChange?.({ id: selectedOption.id, name: selectedOption.label as string });
    }
  };

  const handleNewValueChange = (next: string) => {
    setCurrentNewValue(next);
    onChange?.({ name: next });
  };

  const handlePrimaryClickInternal = useCallback(() => {
    if (!currentSelection) return;

    if (currentSelection === 'new') {
      onPrimaryClick({ name: currentNewValue ?? '' });
      setCurrentNewValue('');
      return;
    }

    const selectedOption = options?.find((option) => option.id === currentSelection);
    if (selectedOption) {
      onPrimaryClick({ id: selectedOption.id, name: selectedOption.label as string });
    }
  }, [currentSelection, currentNewValue, onPrimaryClick, options]);

  const handleClose = () => {
    setCurrentSelection(null);
    setCurrentNewValue('');
    onClose();
  };

  return (
    <ModalWithLayout
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      subtitle={subtitle}
      variant={variant}
      closeOnBackdropClick={closeOnBackdropClick}
      closeOnEscape={closeOnEscape}
      onClickEditTitle={onClickEditTitle}
      showCloseButton={showCloseButton}
      bordered={bordered}
      primaryIcon={primaryIcon}
      primaryIconClassName={primaryIconClassName}
      primaryIconColor={primaryIconColor}
      onClickPrimaryIcon={onClickPrimaryIcon}
      contentClassName={contentClassName}
      footer={{
        primaryButtonText,
        secondaryButtonText,
        onPrimaryClick: handlePrimaryClickInternal,
        onSecondaryClick,
        isPrimaryDisabled,
        isPrimaryLoading,
      }}>
      <RadioList
        name={radioName}
        options={options}
        value={currentSelection ?? undefined}
        onChangeSelection={handleSelectionChange}
        enableNewOption={enableNewOption}
        newOptionLabel={newOptionLabel}
        newOptionPlaceholder={newOptionPlaceholder}
        newMaxLength={newMaxLength}
        newValue={currentNewValue}
        onChangeNewValue={handleNewValueChange}
        newError={newError}
        newOptionHelper={newOptionHelper}
      />
    </ModalWithLayout>
  );
};
